import { EntityDetails } from '../hooks/useEntityDetails';
import { useNavigate } from 'react-router-dom';
import { useContractRead } from 'wagmi';
import { CONFIG } from '../config';
import { Abi } from 'viem';
import { getImageObjectFit } from '../utils/imageDetection';
import { ProductCard } from './ProductCard';
import { useAccount } from 'wagmi';
import { useIpfsImage } from '../hooks/useIpfsImage';
import { Star } from '@phosphor-icons/react';
import { useState, useEffect } from 'react';

interface EntityCardProps {
  entity: EntityDetails;
  clickable?: boolean;
  navigateOnClick?: boolean;
  onClick?: () => void;
  showProducts?: boolean;
}

export function EntityCard({ entity, clickable = true, navigateOnClick = true, onClick, showProducts = false }: EntityCardProps) {
  const navigate = useNavigate();
  const { address } = useAccount();

  // Get entity properties contract address
  const { data: propertiesAddress } = useContractRead({
    address: entity.address as `0x${string}`,
    abi: CONFIG.TRINITY_ENTITY_ABI.abi as Abi,
    functionName: 'properties',
    enabled: !!entity.address,
  }) as { data: `0x${string}` };

  // Get entity image from properties using getUIImage
  const { data: entityImageUrl, error: imageError } = useContractRead({
    address: propertiesAddress as `0x${string}`,
    abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
    functionName: 'getUIImage',
    args: [0], // Get the first image (index 0)
    enabled: !!propertiesAddress && !!entity.address,
  }) as { data: string; error: any };

  // Get entity average rating from TrinityProperties contract
  const { data: averageRating, error: ratingError, isLoading: ratingLoading } = useContractRead({
    address: propertiesAddress as `0x${string}`,
    abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
    functionName: 'getAverageRating',
    args: [],
    enabled: !!propertiesAddress && !!entity.address,
  }) as { data: bigint; error: any; isLoading: boolean };

  // Get entity rating count
  const { data: ratingCount } = useContractRead({
    address: propertiesAddress as `0x${string}`,
    abi: CONFIG.TRINITY_PROPERTIES_ABI.abi as Abi,
    functionName: 'getRatingCount',
    args: [],
    enabled: !!propertiesAddress && !!entity.address,
  }) as { data: bigint };
  
  // Use the IPFS image hook for better gateway handling
  const { url: imageSrc, isLoading: imageLoading, error: ipfsError, hasLoadedOnce, isSuccessfullyLoaded } = useIpfsImage(entityImageUrl, {
    maxRetries: 2,
    onError: (error) => {
      console.error('Failed to load entity image:', error);
    },
    onSuccess: (url) => {
      console.log('Entity image loaded successfully:', url);
    }
  });
  
  // State to track if image should be shown
  const [showImage, setShowImage] = useState(true);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [stableImageUrl, setStableImageUrl] = useState<string>('');

  // Reset image state when imageSrc changes
  useEffect(() => {
    if (imageSrc) {
      setShowImage(true);
      setImageLoadError(false);
    }
  }, [imageSrc]);

  // Only update stable image URL when we have a successful load
  useEffect(() => {
    if (isSuccessfullyLoaded && imageSrc) {
      setStableImageUrl(imageSrc);
    }
  }, [isSuccessfullyLoaded, imageSrc]);

  // Handle image load error
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.error('Entity image failed to load:', {
      src: imageSrc,
      entityName: entity.name,
      entityAddress: entity.address
    });
    setImageLoadError(true);
    setShowImage(false);
  };

  // Handle image load success
  const handleImageLoad = () => {
    console.log('Entity image loaded successfully:', {
      entityName: entity.name,
      imageSrc: imageSrc?.substring(0, 100) + '...'
    });
    setImageLoadError(false);
    setShowImage(true);
  };

  // Debug logging for image loading
  useEffect(() => {
    console.log('EntityCard image state:', {
      entityName: entity.name,
      entityImageUrl,
      imageSrc,
      stableImageUrl,
      showImage,
      imageLoadError,
      hasLoadedOnce,
      isSuccessfullyLoaded,
      isLoading: imageLoading
    });
  }, [entity.name, entityImageUrl, imageSrc, stableImageUrl, showImage, imageLoadError, hasLoadedOnce, isSuccessfullyLoaded, imageLoading]);
  
  // Debug logging
  console.log('=== ENTITY CARD DEBUG ===');
  console.log('Raw image data from contract:', entityImageUrl);
  console.log('Processed image URL:', imageSrc);
  console.log('Is IPFS CID:', entityImageUrl?.includes('|'));
  console.log('========================');
  
  console.log('EntityCard Debug:', {
    entityAddress: entity.address,
    propertiesAddress,
    entityImageUrl,
    imageError,
    imageSrc,
    imageLoading,
    ipfsError,
    hasImageData: !!entityImageUrl,
    imageDataLength: entityImageUrl?.length || 0,
    rawImageData: entityImageUrl,
    processedImageUrl: imageSrc
  });

  const imageObjectFit = getImageObjectFit(entityImageUrl || '');

  const rating = averageRating ? Number(averageRating) : 0;
  const ratingCountNumber = ratingCount ? Number(ratingCount) : 0;

  // Debug logging for entity ratings
  console.log('=== ENTITY CARD DEBUG ===');
  console.log('Entity:', { name: entity.name, address: entity.address });
  console.log('Properties address:', propertiesAddress);
  console.log('Rating data:', { 
    averageRating: averageRating?.toString(), 
    ratingCount: ratingCount?.toString(),
    rating,
    ratingCountNumber,
    ratingError: ratingError?.message,
    ratingLoading
  });
  console.log('==========================');

  // Render star rating with half-star support
  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => {
      const starPosition = index + 1;
      const isFilled = starPosition <= Math.floor(rating);
      const isHalfFilled = !isFilled && (starPosition - rating <= 0.5 && rating % 1 !== 0);
      
      return (
        <div key={index} className="relative inline-block">
          <Star
            size={12}
            weight="regular"
            className="text-gray-400"
          />
          {(isFilled || isHalfFilled) && (
            <div className="absolute inset-0 overflow-hidden">
              <Star
                size={12}
                weight="fill"
                className={`text-yellow-400 ${isHalfFilled ? 'clip-path-half' : ''}`}
                style={isHalfFilled ? { clipPath: 'inset(0 50% 0 0)' } : {}}
              />
            </div>
          )}
        </div>
      );
    });
  };

  const handleEntityInfoClick = () => {
    if (clickable && navigateOnClick) {
      navigate(`/entity/${entity.address}`);
    } else if (clickable && onClick) {
      onClick();
    }
  };

  return (
    <div 
      className="glass-panel p-6 rounded-lg h-full flex flex-col"
    >
      <div 
        className={`flex items-center gap-4 mb-4 ${clickable ? 'cursor-pointer hover:bg-white/5 transition-colors rounded-lg p-2 -m-2' : ''}`}
        onClick={handleEntityInfoClick}
      >
        {/* Entity Image or Icon */}
        <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-black/20 flex items-center justify-center overflow-hidden">
          {stableImageUrl && showImage && !imageLoadError ? (
            <img 
              src={stableImageUrl} 
              alt={entity.name}
              className={`w-full h-full ${imageObjectFit} rounded-xl`}
              onError={handleImageError}
              onLoad={handleImageLoad}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl flex items-center justify-center">
              <span className="text-2xl text-white/60">{entity.name.charAt(0)}</span>
            </div>
          )}
        </div>
        
        {/* Entity Info */}
        <div className="flex-1">
          <h3 className="text-xl font-semibold mb-1">{entity.name}</h3>
          <div className="text-sm text-white/60">
            <div>Products: {entity.products.length}</div>
            {/* Rating Display */}
            <div className="flex items-center gap-1 mt-1">
              {renderStars(rating)}
              <span className="text-xs text-white/60">
                ({ratingCountNumber})
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Products Section */}
      {showProducts && (
        <div className="flex-1">
          <h4 className="text-lg font-semibold mb-4 text-white/80">
            Products
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 max-h-64 overflow-y-auto">
            {entity.products.length > 0 ? (
              entity.products
                .map(product => (
                  <ProductCard
                    key={product.address}
                    product={product}
                    entityName={entity.name}
                    entityAddress={entity.address}
                    hideDetails={true}
                    hideDescription={true}
                  />
                ))
            ) : (
              <p className="text-center text-white/60 col-span-full">
                No products found for this entity
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}